import { Text, TextProps } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

interface AnimatedNumberProps extends Omit<TextProps, 'children'> {
  value: number;
  decimals?: number;
  duration?: number;
  /** When false, value changes are shown instantly (no rolling effect). */
  animate?: boolean;
}

/**
 * Displays a number that rolls from the previous value to the new value.
 * Set `animate={false}` during initial load to suppress the rolling effect;
 * flip to `true` once the baseline value has been established.
 */
const AnimatedNumber = ({
  value,
  decimals = 2,
  duration = 800,
  animate = true,
  ...textProps
}: AnimatedNumberProps) => {
  const [display, setDisplay] = useState(value);
  const prevValue = useRef(value);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const from = prevValue.current;
    prevValue.current = value;

    if (from === value) {
      setDisplay(value);
      return;
    }

    if (!animate) {
      setDisplay(value);
      return;
    }

    const startTime = performance.now();

    const roll = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = from + (value - from) * eased;

      setDisplay(current);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(roll);
      } else {
        setDisplay(value);
      }
    };

    rafRef.current = requestAnimationFrame(roll);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [value, duration, animate]);

  return (
    <Text {...textProps}>
      {display.toFixed(decimals)}
    </Text>
  );
};

export default AnimatedNumber;
